/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useState, useEffect, createContext,
  useContext,
  useMemo,
} from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import qs from 'qs';
import { AdminContext } from '@/contexts/admin-context';
import getEnvVar from '@/utilities/environment-variable';
import sessionStorage, { useSessionAuthData, STORAGE_KEY_AUTH_DATA } from '@/utilities/sessionStorage';
import ExternalAuth from '@/utilities/authorisation';
import { APP_ID } from '@/utilities/authorisation/constants';
import { isEmpty } from '@/utilities/validation';
import { errorHandler } from '@/utilities/error-handling';
import { resolveApiFunction } from '@/utilities/resolve-api-path';
import LoadingScreen from '@/components/loading-screen';
import luiPortalUtilitiesManager from '@/utilities/lui-portal-utilities-manager';
import apiClient from '@/utilities/api-client';
import {
  ROLE_TYPES, getUserPermissions, getUserRoleType, validatePermission, RBAC_PERMISSIONS,
} from '@/utilities/rbac';
import { useLuminatePortalConfig } from '@/contexts/luminate-portal-config-context';

export const AuthContext = createContext();

const PERMISSIONS = [
  'returns_portal_service:app:admin',
  'prereg:read',
  'prereg:write',
];

const parseOAuthHashParams = () => {
  if (typeof window === 'undefined') return null;

  const hash = window.location?.hash;
  if (!hash) return null;

  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const parsed = qs.parse(raw, { ignoreQueryPrefix: true });

  const authorizationCode = parsed.code;
  const token = parsed.id_token;
  const errorCode = parsed.error;

  return {
    authorizationCode,
    token,
    onlyError: errorCode && !token && !authorizationCode ? errorCode : null,
    tokenButError: errorCode && (token || authorizationCode) ? errorCode : null,
  };
};

const hasOAuthRedirectParams = (hashParams) => (
  Boolean(hashParams?.token || hashParams?.authorizationCode || hashParams?.onlyError || hashParams?.tokenButError)
);

const AuthContextProvider = ({ children, localConfig }) => {
  const router = useRouter();
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  const [tokenExpiryTime, setTokenExpiryTime] = useState(null);
  const [loginEmail, setLoginEmail] = useState(null);
  const [loginPassword, setLoginPassword] = useState(null);
  const [externalAuthProvider, setExternalAuthProvider] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loggedInCompanyId, setLoggedInCompanyId] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const isUserLoggedIn = useMemo(() => !!config.access_token, [config]);
  const luminatePortalConfig = useLuminatePortalConfig();
  const hostId = luminatePortalConfig.getHostId() ?? getEnvVar('REACT_APP_ORGANISATION_ID');
  const companyId = luminatePortalConfig.getCompanyId() ?? (router.query?.companyId?.toUpperCase() || hostId);

  const {
    getConfigEnvVar,
    getWidgetModeConfig,
    getRbacConfig,
    adminModeMerchantId,
  } = useContext(AdminContext);

  const {
    enabled: rbacEnabled,
    permissionNameSpace,
  } = getRbacConfig();
  const { enabled: widgetModeFeatureEnabled, isAdminMode: widgetInAdminMode } = getWidgetModeConfig();
  const authUrl = `${hostId}/auth/token`;
  const localExternalAuth = process.env.NODE_ENV === 'development' ?? false;
  let authorizationCode;

  const [sessionData, setSessionData] = useSessionAuthData();

  // Auto-set loggedInCompanyId from LUI preferences when in Luminate portal and not in admin mode
  useEffect(() => {
    const portalCompanyId = luminatePortalConfig.getCompanyId();
    if (portalCompanyId && !adminModeMerchantId) {
      setLoggedInCompanyId(portalCompanyId);
    }
  }, [luminatePortalConfig?.isPortal, luminatePortalConfig?.companyId, adminModeMerchantId]);

  const getAuthRequestBody = async () => {
    if (externalAuthProvider) {
      const hashParams = parseOAuthHashParams();

      if (hashParams) {
        // Take the token from the url hash and use it to get a new token
        authorizationCode = hashParams.authorizationCode;
        const {
          token, onlyError, tokenButError,
        } = hashParams;

        if (tokenButError || onlyError) {
          const error = { response: { authorizationCodeErrorCode: tokenButError || onlyError } };
          return setConfig({
            error: errorHandler.checkInvalidLogin(
              error,
            ),
          });
        }

        if (token || authorizationCode) {
          // Return the token in the format expected by the API
          return qs.stringify({
            grant_type: externalAuthProvider?.grantType,
            [externalAuthProvider?.key]: token || authorizationCode,
          });
        }
      }
    }

    if (externalAuthProvider?.isWidgetLogin) {
      const externalToken = await externalAuthProvider?.getExternalToken();
      return qs.stringify({
        grant_type: externalAuthProvider?.grantType,
        access_token: externalToken?.accessToken,
        scope: externalAuthProvider?.authScopes?.join(' '),
      });
    }

    if (loginEmail && loginPassword) {
      return qs.stringify({
        grant_type: 'staff_password',
        username: loginEmail,
        password: loginPassword,
        organisationId: companyId,
        scope: PERMISSIONS.join(' '),
      });
    }

    return '';
  };

  /**
   *
   * @param {string} reason optional reason message
   */
  const clearAuth = (reason) => {
    setConfig(reason ? { error: reason } : {});
    setUserInfo(null);
    setTokenExpiryTime(null);
    setLoggedInCompanyId(null);
    sessionStorage.clearSession();

    // If we have an external auth provider, log out of that too
    if (externalAuthProvider) {
      externalAuthProvider?.logout();
    }
  };

  const getAllowedRolesConfig = () => {
    const adminRoles = getConfigEnvVar('REACT_APP_ALLOWED_ADMIN_ROLES')?.split(',').filter(Boolean);
    const merchantRoles = getConfigEnvVar('REACT_APP_ALLOWED_MERCHANT_ROLES')?.split(',').filter(Boolean);
    return { adminRoles, merchantRoles };
  };

  const checkIfUsersCompanyIsEnabled = async ({ roleType, organisationId, token }) => {
    switch (roleType) {
      case 'admin': {
        break;
      }
      case 'merchant': {
        const { data } = await resolveApiFunction('/api/company-status', {
          organisationId, token, hostId,
        });

        if (!data.companyEnabled) {
          throw new Error('constants.apiErrors.companyIsDisabled');
        }
        break;
      }
      default:
        throw new Error('constants.apiErrors.permissions');
    }
  };
  const getAuth = async () => {
    async function getAuthToken() {
      setLoading(true);

      try {
        const body = await getAuthRequestBody();
        if (!body) return setLoading(false);

        const response = await apiClient.post(authUrl, body, {
          headers: {
            'x-request-source': APP_ID,
          },
        });

        const newConfig = response.data;
        const roles = newConfig?.doddle?.userInfo?.roles;
        const userRoleType = getUserRoleType(roles, getAllowedRolesConfig());
        const companyIdFromToken = newConfig?.doddle?.userInfo?.organisationId;
        const companyIdToUse = externalAuthProvider?.isWidgetLogin ? companyIdFromToken : companyId;

        await checkIfUsersCompanyIsEnabled({
          roleType: userRoleType,
          organisationId: companyIdToUse,
          token: `${newConfig?.token_type} ${newConfig?.access_token}`,
        });
        const tokenExpiryDelay = (newConfig.expires_in - 300) * 1000;
        const tokenExpiresAt = Date.now() + tokenExpiryDelay;

        setSessionData({
          access_token: newConfig?.access_token,
          token_type: newConfig?.token_type,
          tokenExpiryTime: tokenExpiresAt,
          userInfo: newConfig?.doddle.userInfo,
          loggedInCompanyId: companyIdToUse,
          refresh_token: newConfig?.refresh_token,
        });

        setTokenExpiryTime(tokenExpiresAt);
        setUserInfo(newConfig?.doddle.userInfo);
        setConfig(newConfig);
        setLoading(false);
        setLoggedInCompanyId(companyIdToUse);
      } catch (exception) {
        setConfig({ error: errorHandler.checkInvalidLogin(exception, loginEmail, companyId, authorizationCode) });
        setLoginEmail(null);
        setLoginPassword(null);
        setLoading(false);
      }
      return null;
    }

    const haveAuthInfo = externalAuthProvider
      ? hasOAuthRedirectParams(parseOAuthHashParams())
      : Boolean(companyId) && Boolean(loginEmail);
    if (haveAuthInfo || externalAuthProvider?.isWidgetLogin) {
      await getAuthToken();
    }
  };

  const getRefreshAuth = async () => {
    async function getAuthToken() {
      setLoading(true);
      const getRefreshToken = sessionStorage.getData(STORAGE_KEY_AUTH_DATA)?.refresh_token;
      const body = qs.stringify({
        grant_type: 'refresh_token',
        refresh_token: getRefreshToken,
      });
      try {
        const response = await apiClient.post(authUrl, body, {
          headers: {
            'x-request-source': APP_ID,
          },
        });

        const newConfig = response.data;

        if (!newConfig?.access_token) {
          clearAuth();
          return;
        }

        const tokenExpiryDelay = (newConfig.expires_in - 300) * 1000;
        const tokenExpiresAt = Date.now() + tokenExpiryDelay;
        const sessionUserInfo = sessionStorage.getData(STORAGE_KEY_AUTH_DATA)?.userInfo || {};
        setSessionData({
          access_token: newConfig?.access_token,
          token_type: newConfig?.token_type,
          tokenExpiryTime: tokenExpiresAt,
          userInfo: sessionUserInfo,
          loggedInCompanyId: companyId,
          refresh_token: getRefreshToken,
        });

        setTokenExpiryTime(tokenExpiresAt);
        setUserInfo(newConfig?.doddle.userInfo);
        setConfig(newConfig);
        setLoading(false);
        setLoggedInCompanyId(companyId);
      } catch (exception) {
        setLoading(false);
        clearAuth();
      }
    }

    const haveAuthInfo = externalAuthProvider
      ? hasOAuthRedirectParams(parseOAuthHashParams())
      : Boolean(companyId);
    if (haveAuthInfo) {
      await getAuthToken();
    }
  };

  const checkSessionIsValid = (authSessionData) => {
    let isValid = true;
    const sessionUserInfo = authSessionData.userInfo || {};
    if (!sessionUserInfo.userId) {
      isValid = false;
    }
    const sessionUserRoleType = getUserRoleType(sessionUserInfo.roles, getAllowedRolesConfig());
    if (sessionUserRoleType === ROLE_TYPES.UNKNOWN) {
      isValid = false;
    }
    return isValid;
  };

  const getIsUserLoggedIn = () => {
    if (externalAuthProvider?.isWidgetLogin) {
      return true;
    }
    return isUserLoggedIn;
  };

  useEffect(() => {
    if (sessionChecked) return;

    if (widgetModeFeatureEnabled && luiPortalUtilitiesManager.isInIFrame() && !sessionChecked) {
      setSessionChecked(true);
      return;
    }

    const restoreSession = async () => {
      if (isEmpty(sessionData)) {
        setSessionChecked(true);
        return;
      }

      const companyIdFromSession = sessionData?.loggedInCompanyId;

      if (!externalAuthProvider?.isWidgetLogin && companyIdFromSession && companyId !== companyIdFromSession) {
        clearAuth();
        setLoading(false);
        setSessionChecked(true);
        sessionStorage.startSession(companyId);
        return;
      }

      if (!checkSessionIsValid(sessionData)) {
        clearAuth();
        setLoading(false);
        setSessionChecked(true);
      } else {
        await getRefreshAuth();
        setSessionChecked(true);
      }
    };

    void restoreSession();
  }, []);

  useEffect(() => {
    if (!config?.access_token && localConfig?.isDevLogin) {
      setLoginEmail(localConfig.username);
      setLoginPassword(localConfig.password);
    }
  }, []);

  useEffect(() => {
    const fetchUserPermissions = async () => {
      const permissions = await getUserPermissions();
      setUserPermissions(permissions);
      setPermissionsChecked(true);
    };

    if (getIsUserLoggedIn() && !permissionsChecked) {
      if (rbacEnabled) {
        void fetchUserPermissions();
      } else {
        setPermissionsChecked(true);
      }
    }
  }, [getIsUserLoggedIn()]);

  useEffect(() => {
    if (loginEmail && loginPassword) {
      void getAuth();
    }
  }, [loginEmail, loginPassword]);

  useEffect(() => {
    // SSO redirect case
    if (externalAuthProvider && window?.location?.hash) {
      void getAuth();
    }
  }, [externalAuthProvider]);

  const refreshTokenIfExpired = () => {
    const isTokenExpired = () => Date.now() > tokenExpiryTime;
    // If the token is expired and we don't have an external auth provider, we need to refresh the token
    if (isTokenExpired() && !externalAuthProvider) {
      void getRefreshAuth();
    } else if (isTokenExpired() && externalAuthProvider) {
      // If the token is expired and we have an external auth provider, we need to clear the auth
      clearAuth('constants.apiErrors.codeExpired');
    }
  };

  useEffect(() => {
    let interval;
    if (tokenExpiryTime) {
      interval = setInterval(refreshTokenIfExpired, 10000);
      return () => {
        clearInterval(interval);
      };
    }
    return () => {};
  }, [tokenExpiryTime]);

  const getAccessToken = () => config?.access_token ?? '';
  const getTokenType = () => config?.token_type ?? '';
  const getToken = () => `${getTokenType()} ${getAccessToken()}`;
  const getTokenExpiresIn = () => config?.expires_in ?? '';
  const getUserId = () => userInfo?.userId ?? '';
  const createExternalAuthProvider = (authProps) => {
    const authProvider = ExternalAuth(authProps, localExternalAuth);
    setExternalAuthProvider(authProvider);
  };

  const hasPermission = (permission) => {
    if (!rbacEnabled) {
      return true;
    }
    return validatePermission({
      userPermissions,
      permissionNameSpace,
      permissionToValidate: permission,
    });
  };

  /**
   * Validates if user has a matching role to be able
   * to access the portal in widget mode
   */
  const checkWidgetPermissions = () => {
    let valid = true;

    if (!hasPermission(RBAC_PERMISSIONS.config.read)) {
      valid = false;
      clearAuth('constants.apiErrors.permissions');
    } else if (widgetInAdminMode && !hasPermission(RBAC_PERMISSIONS.company.admin)) {
      valid = false;
      clearAuth('constants.apiErrors.adminPermissions');
    }

    return valid;
  };

  if (!sessionChecked) {
    return <LoadingScreen />;
  }

  if (isUserLoggedIn && rbacEnabled && !permissionsChecked) {
    return <LoadingScreen />;
  }

  return (
    <AuthContext.Provider value={{
      getAccessToken,
      getTokenType,
      getToken,
      getTokenExpiresIn,
      getAuth,
      getUserId,
      getAllowedRolesConfig,
      setLoginEmail,
      setLoginPassword,
      createExternalAuthProvider,
      externalAuthProvider,
      isUserLoggedIn: getIsUserLoggedIn(),
      loading,
      loggedInCompanyId,
      clearAuth,
      checkWidgetPermissions,
      getRefreshAuth,
      hasPermission,
      permissionsChecked,
      ...config,
    }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthContextProvider.defaultProps = {
  localConfig: null,
};

AuthContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  localConfig: PropTypes.shape({
    username: PropTypes.string,
    password: PropTypes.string,
    rootOrgId: PropTypes.string,
    isDevLogin: PropTypes.bool,
  }),
};

export default AuthContextProvider;
